import { ref } from 'vue'
import { db, auth } from '@/firebase/firebase'
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore'

/**
 * 관심 등록(상담 신청) 내역 관리 Composable
 * - 사용자가 상담 신청한 현장 목록 조회
 * - consultationRequests 컬렉션에서 customerPhone 기준으로 조회
 */

export function useInterest() {
  const interests = ref([])
  const loading = ref(false)

  /**
   * 현재 사용자 UID 가져오기
   */
  const getCurrentUserId = () => {
    return auth.currentUser?.uid || null
  }

  /**
   * 관심 등록(상담 신청) 내역 로드
   * - 로그인 사용자의 customerId 기준으로 조회
   */
  const loadInterests = async () => {
    const userId = getCurrentUserId()
    if (!userId) {
      interests.value = []
      return []
    }

    loading.value = true
    try {
      // customerId로 상담 신청 내역 조회
      const q = query(
        collection(db, 'consultationRequests'),
        where('customerId', '==', userId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      interests.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))

      return interests.value
    } catch (e) {
      console.error('관심 등록 내역 로드 실패:', e)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 관심 등록 개수 가져오기
   */
  const getInterestCount = async () => {
    const userId = getCurrentUserId()
    if (!userId) return 0

    try {
      const q = query(
        collection(db, 'consultationRequests'),
        where('customerId', '==', userId)
      )

      const snapshot = await getDocs(q)
      return snapshot.size
    } catch {
      return 0
    }
  }

  /**
   * 특정 현장에 관심 등록(상담 신청) 했는지 확인
   * @param {string} propertyId 현장 ID
   */
  const hasInterest = async (propertyId) => {
    const userId = getCurrentUserId()
    if (!userId) return false

    try {
      const q = query(
        collection(db, 'consultationRequests'),
        where('customerId', '==', userId),
        where('propertyId', '==', propertyId)
      )

      const snapshot = await getDocs(q)
      return !snapshot.empty
    } catch {
      return false
    }
  }

  return {
    interests,
    loading,
    loadInterests,
    getInterestCount,
    hasInterest
  }
}
